import { useEffect, useRef } from 'react';

/**
 * Generic polling hook. Calls `fetcher()` every `intervalMs` while `enabled` is true.
 *
 * Options:
 *   - `enabled` (default true)   — start/stop the timer.
 *   - `immediate` (default true) — fire `fetcher()` once on mount, then
 *     every `intervalMs`. Set false when the caller already has seed data
 *     (e.g. from the case-page dashboard envelope) and only wants the
 *     poll cadence for freshness — avoids a redundant on-mount fetch
 *     and the pool slot it would consume.
 *
 * Automatically cleans up on unmount or when dependencies change.
 */
export function usePolling(fetcher, intervalMs, enabled = true, immediate = true) {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!enabled) return;

    let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        await fetcherRef.current();
      } catch {
        // Polling errors are non-fatal — next tick retries
      }
    };

    if (immediate) poll();
    const id = setInterval(poll, intervalMs);
    return () => { active = false; clearInterval(id); };
  }, [intervalMs, enabled, immediate]);
}
